<script setup lang="ts">
/* UTILS */
import { AfEmptyState, AfModal } from '@af-lib/ui-lib';
import { ALButton, ALLoader } from '@af-lib/vue-ui';
import { computed, ref, watch } from 'vue';

/* HELPERS */
import { displayGenericErrorToaster, flatMapIt } from '@/api/helpers/helpers';
import { useRetryBatchMutation } from '@/api/vue-query/batches/batches.mutations';
/* API */
import { useGetBatchesQuery } from '@/api/vue-query/batches/batches.queries';
/* COMPONENTS */
import Text from '@/components/text/Text.vue';

import BatchCard, { type BatchInfo } from './BatchCard.vue';
import BatchesSkeleton from './BatchesSkeleton.vue';
/* COMPOSABLES */
import { useBatchesNotifs } from './useBatchesNotifs';

/* API */
const {
  data: batchesData,
  isFetching: isFetchingBatches,
  isFetchingNextPage,
  fetchNextPage,
  hasNextPage,
  isError: isFetchingBatchesError
} = useGetBatchesQuery();

const { mutate: retryBatch, isPending: retryingBatch, isError } = useRetryBatchMutation();

/* VARIABLES */
const batchesList = computed(() => flatMapIt(batchesData.value?.pages));

const isErrorModalDisplay = ref(false);
const currentBatch = ref<BatchInfo>();

const { displayedBatches } = useBatchesNotifs(batchesList);

/* FUNCTIONS */
function showErrorModal(batchData) {
  isErrorModalDisplay.value = true;
  currentBatch.value = batchData;
}

function onBottomReached(isVisible) {
  if (isVisible && !isFetchingBatches.value) {
    fetchNextPage();
  }
}

function retry() {
  if (currentBatch.value) {
    retryBatch(currentBatch.value, {
      onSuccess: () => {
        isErrorModalDisplay.value = false;
      }
    });
  }
}

watch([isError, isFetchingBatchesError], () => {
  if (isError.value || isFetchingBatchesError) {
    displayGenericErrorToaster();
  }
});
</script>

<template>
  <div class="mx-5 my-10 grow d-flex flex-column">
    <Text type="body3" class="mb-5"> {{ $t('recruiter_network.queue.panel.body') }} </Text>

    <!-- LOADING STATE -->
    <BatchesSkeleton v-if="isFetchingBatches && !isFetchingNextPage" />
    <!-- EMPTY STATE -->
    <AfEmptyState
      v-else-if="!batchesList.length"
      type="embedded"
      :text="$t('recruiter_network.queue.panel.empty_state')"
      style="margin-top: 87px"
    />

    <!-- LIST -->
    <v-virtual-scroll v-else :height="600" :itemHeight="134" :items="displayedBatches">
      <template #default="{ item, index }: { item: any; index: number }">
        <BatchCard :batch="item" @showErrors="showErrorModal" />
        <!-- INFINITE SCROLL MANAGEMENT -->
        <div v-if="index === displayedBatches.length - 1 && hasNextPage" class="d-flex justify-center py-2">
          <ALLoader v-intersect.quiet="onBottomReached" size="s" />
        </div>
      </template>
    </v-virtual-scroll>

    <!--ERROR MODAL -->
    <Teleport to="#af-app">
      <AfModal
        v-model="isErrorModalDisplay"
        iconHeader="warning"
        :title="$t('recruiter_network.queue.modal.report.title', { value: new Date() })"
      >
        <template #content>
          <v-card v-if="currentBatch" class="py-3 px-4 d-flex flex-column gap-20 bg-sectionSecondaryBg">
            <Text type="body2" emphasis="medium"> {{ $t(currentBatch.label) }} </Text>
            <div class="d-flex flex-column gap-12">
              <Text type="body3">
                {{
                  $t('recruiter_network.queue.modal.report.subtitle', {
                    fail: currentBatch.nb_failed_operations
                  })
                }}
              </Text>
              <ul class="ps-4 overflow-auto" style="max-height: 220px">
                <li v-for="error in new Set(currentBatch.errors_excerpt.map(error => error.email))" :key="error">
                  <Text type="body3"> {{ error }}</Text>
                </li>
              </ul>
            </div>
          </v-card>
        </template>
        <template #footer>
          <ALButton type="tertiary" :label="$t('general.close')" @click="isErrorModalDisplay = false" />
          <ALButton type="primary" :isLoading="retryingBatch" :label="$t('general.retry')" @click="retry" />
        </template>
      </AfModal>
    </Teleport>
  </div>
</template>
